// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Ask a colleague, the pure parts: which asks wait on a request, which answered ask a thread
// message belongs to, and the "(answered by …)" the agent got with a written answer.

import {type AgentAnswer, type AgentAsk, agentAskSignature} from '@konvo/schema/src/domains/konvo/Agents';

/** The asks still out with someone for one request, oldest first. */
export function waitingAsks(asks: ReadonlyArray<AgentAsk>, requestId: string): Array<AgentAsk> {
	return asks.filter((ask) => ask.requestId === requestId && ask.status === 'waiting');
}

/**
 * The colleague's answer a thread message shows: an answered ask with the same questions, the one
 * answered closest to the message. The agent tool records no request id on the message.
 */
export function askForAnswers(
	asks: ReadonlyArray<AgentAsk>,
	pairs: ReadonlyArray<AgentAnswer>,
	createdAt: string,
): AgentAsk | null {
	const questions = pairs.map((pair) => pair.question).join('\u0000');
	const at = Date.parse(createdAt);
	let best: AgentAsk | null = null;
	let bestGap = Number.POSITIVE_INFINITY;
	for (const ask of asks) {
		if (ask.status !== 'answered' || !ask.answer) continue;
		if (ask.answer.map((pair) => pair.question).join('\u0000') !== questions) continue;
		const gap = Math.abs((Date.parse(ask.answeredAt ?? ask.createdAt) || 0) - (at || 0));
		if (gap < bestGap) {
			best = ask;
			bestGap = gap;
		}
	}
	return best;
}

/** A written answer without the "(answered by …)" the agent got with it. */
export function unsigned(pairs: ReadonlyArray<AgentAnswer>, name: string): Array<AgentAnswer> {
	const signature = agentAskSignature(name);
	return pairs.map((pair) =>
		pair.answer.endsWith(signature) ? {...pair, answer: pair.answer.slice(0, -signature.length)} : pair,
	);
}

/** The first name, as cards say "JP's agent asks" and "Asked Lerato". */
export const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;
